<template>
  <div v-if="show" :class="rootClasses">
    <div class="loader-inner">
      <div class="loader-brand">
        <Logo brand="one-vision-shop" :size="logoScale" />
      </div>
      <p v-if="displayMessage" class="loader-msg" aria-live="polite">
        {{ displayMessage
        }}<span class="loader-dots" aria-hidden="true"
          ><span class="loader-dot"> .</span><span class="loader-dot">.</span
          ><span class="loader-dot">.</span></span
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Logo from "@/components/theme/ui/Logo.vue";

type LoaderLayout = "overlay" | "inline" | "embed" | "mainColumn";
type LogoScale = "sm" | "md" | "lg";

defineOptions({ name: "Loader" });

const props = withDefaults(
  defineProps<{
    show?: boolean;
    layout?: LoaderLayout;
    mainColumnSidebarCollapsed?: boolean;
    logoScale?: LogoScale;
    contain?: boolean;
    message?: string;
  }>(),
  {
    show: true,
    layout: "overlay",
    mainColumnSidebarCollapsed: false,
    logoScale: "md",
    contain: false,
    message: "",
  },
);

const displayMessage = computed(() => (props.message || "").trim());

const rootClasses = computed(() => {
  if (props.layout === "overlay") {
    return props.contain
      ? "loader-root loader-root--overlay-contained"
      : "loader-root loader-root--overlay";
  }
  if (props.layout === "mainColumn") {
    return [
      "loader-root loader-root--main-column",
      props.mainColumnSidebarCollapsed
        ? "loader-root--main-column-collapsed"
        : "loader-root--main-column-expanded",
    ];
  }
  if (props.layout === "inline") {
    return "loader-root loader-root--inline";
  }
  return "loader-root loader-root--embed";
});
</script>

<style scoped>
.loader-root--overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  animation: loader-fade-in 0.1s ease-out;
}

.loader-root--overlay-contained {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(2px);
  animation: loader-fade-in 0.1s ease-out;
  border-radius: inherit;
}

/* Columna principal (AppLayout): mismo rectángulo siempre, no depende del alto del contenido */
.loader-root--main-column {
  position: fixed;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  animation: loader-fade-in 0.1s ease-out;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

@media (min-width: 1024px) {
  .loader-root--main-column-expanded {
    left: var(--sidebar-default-width);
  }

  .loader-root--main-column-collapsed {
    left: var(--sidebar-collapsed-width);
  }
}

.loader-root--inline {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 7rem;
  width: 100%;
}

.loader-root--embed {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  max-width: 22rem;
  padding: 0 1rem;
}

.loader-msg {
  margin: 0;
  text-align: center;
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--theme-neutral-dark, #374151);
}

.loader-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.04));
  animation: loader-breathe 1.5s cubic-bezier(0.45, 0, 0.55, 1) infinite;
  will-change: transform, opacity;
}

@keyframes loader-breathe {
  0%,
  100% {
    opacity: 0.88;
    transform: scale(1);
  }
  50% {
    opacity: 0.78;
    transform: scale(0.985);
  }
}

.loader-dots {
  display: inline-block;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.loader-dot {
  display: inline-block;
  animation: loader-dot-bounce 1.05s ease-in-out infinite;
  padding-left: 0.25em;
}

.loader-dot:nth-child(1) {
  animation-delay: 0s;
}

.loader-dot:nth-child(2) {
  animation-delay: 0.18s;
}

.loader-dot:nth-child(3) {
  animation-delay: 0.36s;
}

@keyframes loader-dot-bounce {
  0%,
  40%,
  100% {
    transform: translateY(0);
  }
  20% {
    transform: translateY(-0.28em);
  }
}

@keyframes loader-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
